/**
 * ULD外放管理Modal组件
 * 支持单个ULD外放和撤销外放操作
 */
import React, { useState, useEffect } from 'react';
import {
    Modal,
    Form,
    Input,
    Button,
    Alert,
    Space,
    Typography,
    Divider,
    Row,
    Col,
    Tag
} from 'antd';
import { ExportOutlined, ImportOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;
const { TextArea } = Input;

const UldDeliveryModal = ({
    visible,
    onCancel,
    onConfirm,
    loading = false,
    uld = null,
    mode = 'deliver' // 'deliver' | 'revoke'
}) => {
    const [form] = Form.useForm();
    const [confirmStep, setConfirmStep] = useState(false);
    // 缓存第一步表单值：进入第二步后 <Form> 会被卸载，antd 会丢掉字段值，
    // 因此必须在切换前把值缓存到 state，第二步的展示和提交都从这里读
    const [formValues, setFormValues] = useState({});

    const isDelivery = mode === 'deliver';
    const isRevoke = mode === 'revoke';

    // 切回第一步（点"上一步"）时把缓存值塞回 form，避免用户重填
    useEffect(() => {
        if (!confirmStep && Object.keys(formValues).length > 0) {
            form.setFieldsValue(formValues);
        }
    }, [confirmStep, formValues, form]);

    const handleSubmit = async () => {
        try {
            if (!confirmStep) {
                const values = await form.validateFields();
                setFormValues(values);
                setConfirmStep(true);
                return;
            }

            // 第二步：从缓存读取（form 实例已被第二步卸载，字段值已丢失）
            let requestData;
            if (mode === 'deliver') {
                requestData = {
                    consignee_name: formValues.consigneeName,
                    remarks: formValues.remarks
                };
            } else { // revoke
                requestData = {
                    reason: formValues.reason
                };
            }

            await onConfirm(requestData);

            setConfirmStep(false);
            setFormValues({});
            form.resetFields();
        } catch (error) {
            console.error('表单验证失败:', error);
        }
    };

    const handleCancel = () => {
        setConfirmStep(false);
        setFormValues({});
        form.resetFields();
        onCancel();
    };

    const handleBack = () => {
        setConfirmStep(false);
    };

    const getModalTitle = () => {
        if (confirmStep) {
            return isDelivery ? '确认外放ULD' : '确认撤销外放';
        }
        return isDelivery ? 'ULD外放给收货人' : '撤销ULD外放';
    };

    const getIcon = () => {
        return isDelivery ? <ExportOutlined /> : <ImportOutlined />;
    };

    const getStatusColor = (status) => {
        const statusColors = {
            'IN_STOCK': 'green',
            'IN_TRANSIT': 'blue',
            'OUT_OF_SERVICE': 'orange',
            'RETIRED': 'red',
            'LOST': 'purple'
        };
        return statusColors[status] || 'default';
    };

    const getAvailabilityColor = (availability) => {
        const availabilityColors = {
            'AVAILABLE': 'green',
            'OCCUPIED': 'orange',
            'UNSERVICEABLE': 'red'
        };
        return availabilityColors[availability] || 'default';
    };

    return (
        <Modal
            title={
                <Space>
                    {getIcon()}
                    {getModalTitle()}
                </Space>
            }
            open={visible}
            onCancel={handleCancel}
            width={600}
            footer={null}
            destroyOnHidden
        >
            {/* ULD基本信息展示 */}
            {uld && (
                <>
                    <div style={{ marginBottom: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Text strong>ULD识别码: </Text>
                                <Text code>{uld.master?.uld_identifier}</Text>
                            </Col>
                            <Col span={12}>
                                <Text strong>ULD类型: </Text>
                                <Text>{uld.master?.uld_type}</Text>
                            </Col>
                        </Row>
                        <Row gutter={16} style={{ marginTop: 8 }}>
                            <Col span={12}>
                                <Text strong>核心状态: </Text>
                                <Tag color={getStatusColor(uld.stock?.core_status)}>
                                    {uld.stock?.core_status}
                                </Tag>
                            </Col>
                            <Col span={12}>
                                <Text strong>可用性: </Text>
                                <Tag color={getAvailabilityColor(uld.stock?.availability)}>
                                    {uld.stock?.availability}
                                </Tag>
                            </Col>
                        </Row>
                        {uld.master?.delivered_to_consignee && (
                            <Row style={{ marginTop: 8 }}>
                                <Col span={24}>
                                    <Text strong>当前收货人: </Text>
                                    <Text>{uld.master?.consignee_name}</Text>
                                </Col>
                            </Row>
                        )}
                    </div>
                    <Divider />
                </>
            )}

            {!confirmStep ? (
                // 第一步：输入表单
                <Form
                    form={form}
                    layout="vertical"
                    requiredMark={false}
                >
                    {/* 状态检查警告 */}
                    {isDelivery && uld?.stock?.core_status !== 'IN_STOCK' && (
                        <Alert
                            type="error"
                            message="操作受限"
                            description={`只有在库状态的ULD才能外放，当前状态为：${uld?.stock?.core_status}`}
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {isDelivery && uld?.master?.delivered_to_consignee && (
                        <Alert
                            type="warning"
                            message="ULD已外放"
                            description={`该ULD已外放给：${uld?.master?.consignee_name}`}
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {isRevoke && !uld?.master?.delivered_to_consignee && (
                        <Alert
                            type="warning"
                            message="ULD未外放"
                            description="该ULD当前未处于外放状态"
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {isDelivery && (
                        <Form.Item
                            name="consigneeName"
                            label="收货人名称"
                            rules={[
                                { required: true, message: '请输入收货人名称' },
                                { max: 200, message: '收货人名称不能超过200个字符' }
                            ]}
                        >
                            <Input
                                placeholder="请输入收货人名称（货代、物流公司等）"
                                maxLength={200}
                                showCount
                            />
                        </Form.Item>
                    )}

                    {isRevoke && (
                        <Form.Item
                            name="reason"
                            label="撤销原因"
                            rules={[
                                { required: true, message: '请输入撤销原因' },
                                { max: 500, message: '撤销原因不能超过500个字符' }
                            ]}
                        >
                            <TextArea
                                rows={3}
                                placeholder="请说明撤销外放的原因..."
                                maxLength={500}
                                showCount
                            />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="remarks"
                        label={isDelivery ? "外放备注" : "补充说明"}
                    >
                        <TextArea
                            rows={3}
                            placeholder={isDelivery ? "选填，外放相关备注信息..." : "选填，补充说明信息..."}
                            maxLength={500}
                            showCount
                        />
                    </Form.Item>

                    <div style={{ textAlign: 'right', marginTop: 24 }}>
                        <Space>
                            <Button onClick={handleCancel}>
                                取消
                            </Button>
                            <Button 
                                type="primary" 
                                onClick={handleSubmit}
                                disabled={
                                    (isDelivery && uld?.stock?.core_status !== 'IN_STOCK') ||
                                    (isDelivery && uld?.master?.delivered_to_consignee) ||
                                    (isRevoke && !uld?.master?.delivered_to_consignee)
                                }
                            >
                                下一步
                            </Button>
                        </Space>
                    </div>
                </Form>
            ) : (
                // 第二步：确认操作
                <div>
                    <Alert
                        type="warning"
                        message={isDelivery ? "确认外放操作" : "确认撤销操作"}
                        description={
                            isDelivery 
                                ? "外放后，ULD的可用性将变为占用状态，不会计入可用库存统计。请确认操作无误。"
                                : "撤销外放后，ULD的可用性将恢复为可用状态，重新计入可用库存统计。请确认操作无误。"
                        }
                        style={{ marginBottom: 16 }}
                    />

                    <div style={{ padding: 16, backgroundColor: '#fafafa', borderRadius: 6, marginBottom: 16 }}>
                        <Title level={5}>操作详情</Title>
                        {isDelivery && (
                            <div>
                                <Text strong>收货人：</Text>
                                <Text>{formValues.consigneeName}</Text>
                            </div>
                        )}
                        {isRevoke && (
                            <div>
                                <Text strong>撤销原因：</Text>
                                <Text>{formValues.reason}</Text>
                            </div>
                        )}
                        {formValues.remarks && (
                            <div style={{ marginTop: 8 }}>
                                <Text strong>备注：</Text>
                                <Text>{formValues.remarks}</Text>
                            </div>
                        )}
                    </div>

                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={handleBack}>
                                上一步
                            </Button>
                            <Button onClick={handleCancel}>
                                取消
                            </Button>
                            <Button 
                                type="primary" 
                                danger={isRevoke}
                                loading={loading}
                                onClick={handleSubmit}
                            >
                                {isDelivery ? '确认外放' : '确认撤销'}
                            </Button>
                        </Space>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default UldDeliveryModal; 